import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Search, SlidersHorizontal, X, Mic } from "lucide-react";

export const SearchDock = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ city: "", type: "", collection: "", price_max: "" });

  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) qs.append(k, v); });
    nav(`/biens-off-market?${qs.toString()}`);
    setOpen(false);
  };

  const selectCls = "bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#C19A6B] hover:border-white/30 transition-colors";
  const inputCls = "bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-200 focus:outline-none focus:border-[#C19A6B] hover:border-white/30 transition-colors";

  return (
    <motion.div
      data-testid="search-dock"
      initial={{ y: 120, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed bottom-0 left-0 w-full z-[60] pointer-events-none"
    >
      <div className="w-full pointer-events-auto">
        {/* Mobile expandable panel */}
        <AnimatePresence>
          {open && (
            <motion.form
              onSubmit={submit}
              data-testid="search-dock-mobile-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="sm:hidden mx-2 mb-3 bg-[#0d0d10] backdrop-blur-xl border border-white/10 rounded-[5px] p-4 space-y-3 shadow-2xl"
            >
              <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder={t("search.city")} data-testid="dock-city-m" className={`${inputCls} w-full`} />
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="dock-type-m" className={`${selectCls} w-full`}>
                <option value="">{t("search.type_full")}</option><option value="Appartement">{t("search.apartment")}</option><option value="Maison">{t("search.house")}</option>
              </select>
              <select value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} data-testid="dock-collection-m" className={`${selectCls} w-full`}>
                <option value="">{t("search.collection")}</option><option>Habitat</option><option>Signature</option><option>Prestige</option>
              </select>
              <select value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value })} data-testid="dock-budget-m" className={`${selectCls} w-full`}>
                <option value="">{t("search.budget_max")}</option><option value="200000">200 000 €</option><option value="300000">300 000 €</option><option value="500000">500 000 €</option><option value="1000000">1 000 000 €</option><option value="">{t("search.no_limit")}</option>
              </select>
              <button type="submit" data-testid="dock-submit-m" className="w-full flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm rounded-full py-3 transition-colors">
                <Search className="w-4 h-4" /> {t("search.search")}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Docked bar — pleine largeur, contour doré */}
        <div className="bg-[#0d0d10] backdrop-blur-xl border-y border-[#C19A6B]/40 shadow-2xl shadow-black/40 px-4 sm:px-6 py-3 max-sm:bg-transparent max-sm:border-0 max-sm:shadow-none max-sm:backdrop-blur-none max-sm:p-0">
          {/* Desktop inline form — centré à la largeur du menu */}
          <form onSubmit={submit} data-testid="search-dock-form" className="hidden sm:flex items-center gap-2 w-full max-w-7xl mx-auto">
            <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder={t("search.city")} data-testid="dock-city" className={`${inputCls} flex-1 min-w-0`} />
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="dock-type" className={`${selectCls} flex-1 min-w-0`}>
              <option value="">{t("search.type")}</option><option value="Appartement">{t("search.apartment")}</option><option value="Maison">{t("search.house")}</option>
            </select>
            <select value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} data-testid="dock-collection" className={`${selectCls} flex-1 min-w-0`}>
              <option value="">{t("search.collection")}</option><option>Habitat</option><option>Signature</option><option>Prestige</option>
            </select>
            <select value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value })} data-testid="dock-budget" className={`${selectCls} flex-1 min-w-0`}>
              <option value="">{t("search.budget_max")}</option><option value="200000">200 000 €</option><option value="300000">300 000 €</option><option value="500000">500 000 €</option><option value="1000000">1 000 000 €</option><option value="">{t("search.no_limit")}</option>
            </select>
            <button type="submit" data-testid="dock-submit" aria-label={t("search.search")} title={t("search.search")} className="shrink-0 flex items-center justify-center bg-white hover:bg-zinc-200 text-[#050505] rounded-full w-11 h-11 transition-colors">
              <Search className="w-5 h-5" />
            </button>
            <Link to="/espace-acquereurs" data-testid="dock-voice" className="shrink-0 inline-flex items-center gap-1.5 h-11 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-bold uppercase tracking-wider rounded-full border-2 border-[#C19A6B] px-4 whitespace-nowrap transition-colors">
              <Mic className="w-4 h-4" /> {t("search.voice")}
            </Link>
          </form>

          {/* Mobile — 2 boutons pleine largeur, collés en bas */}
          <div className="sm:hidden grid grid-cols-2 gap-0">
            <a href="/proposer" data-testid="dock-create-mobile" className="flex flex-col items-center justify-center gap-0.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white px-2 py-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-center leading-tight whitespace-nowrap">{t("search.create")}</span>
              <span className="calli text-base text-white leading-none">{t("search.create_sub")}</span>
            </a>
            <a href="/espace-acquereurs" data-testid="search-dock-toggle" className="flex flex-col items-center justify-center gap-0.5 bg-white hover:bg-zinc-200 text-[#050505] px-2 py-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-center leading-tight whitespace-nowrap">{t("search.find")}</span>
              <span className="calli text-base text-[#C19A6B] leading-none">{t("search.find_sub")}</span>
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
